import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, LogOut, UserCircle, Settings as SettingsIcon, Radio, Bot, Coins } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarContent } from "@/components/layout/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { useCoins } from "@/context/CoinContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export function Topbar() {
    const { user, logout } = useAuth();
    const { balance } = useCoins();
    const navigate = useNavigate();
    const [session, setSession] = useState({ monitoring: false, ai_status: "idle", current_game: null });
    const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
    const [open, setOpen] = useState(false);

    const load = useCallback(() => {
        return Promise.all([api.get("/session"), api.get("/notifications")])
            .then(([s, n]) => { setSession(s.data); setNotifs(n.data); })
            .catch(() => { /* ignore */ });
    }, []);

    // Instant status update when monitoring starts/stops elsewhere (e.g. Live Capture).
    const onSession = useCallback((e) => {
        const d = e.detail || {};
        setSession((s) => ({ ...s, ...d }));
        load();
    }, [load]);

    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        window.addEventListener("ww:session", onSession);
        return () => { clearInterval(t); window.removeEventListener("ww:session", onSession); };
    }, [load, onSession]);

    const markAll = async () => {
        await api.post("/notifications/read-all");
        load();
    };

    const monitoring = session.monitoring;

    return (
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-[#0a0a0a]/90 backdrop-blur-md flex items-center justify-between px-4 lg:px-7">
            <div className="flex items-center gap-3">
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild>
                        <button data-testid="mobile-menu-btn" className="lg:hidden grid place-items-center w-9 h-9 rounded-sm border border-border hover:bg-white/5">
                            <Menu className="w-5 h-5" />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 w-64 bg-[#0c0c0c] border-border">
                        <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                        <SheetDescription className="sr-only">Browse WhiteWater AI sections</SheetDescription>
                        <SidebarContent onNavigate={() => setOpen(false)} />
                    </SheetContent>
                </Sheet>

                <div className="flex items-center gap-2">
                    <span
                        data-testid="monitoring-status"
                        className={cn(
                            "inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs font-mono uppercase tracking-wider",
                            monitoring ? "text-success border-success/40 bg-success/10" : "text-muted-foreground border-border bg-surface"
                        )}
                    >
                        <span className={cn("w-2 h-2 rounded-full", monitoring ? "bg-success ww-live-dot" : "bg-muted-foreground")} />
                        {monitoring ? "Monitoring" : "Idle"}
                    </span>
                    <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-border bg-surface text-xs font-mono text-muted-foreground">
                        <Bot className="w-3.5 h-3.5 text-volt" />
                        AI: <span className="text-white uppercase">{session.ai_status}</span>
                    </span>
                    {session.current_game && (
                        <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-mono"
                            style={{ borderColor: `${session.current_game.color}55`, color: session.current_game.color }}>
                            <Radio className="w-3.5 h-3.5" />
                            {session.current_game.short}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button onClick={() => navigate("/challenges")} data-testid="coin-chip"
                    className="flex items-center gap-1.5 px-3 h-9 rounded-sm border border-volt/30 bg-volt/10 hover:bg-volt/20 transition-colors">
                    <Coins className="w-4 h-4 text-volt" />
                    <span className="text-sm font-mono font-bold text-volt" data-testid="coin-chip-balance">{balance.toLocaleString()}</span>
                </button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button data-testid="notifications-btn" className="relative grid place-items-center w-9 h-9 rounded-sm border border-border hover:bg-white/5">
                            <Bell className="w-[18px] h-[18px]" />
                            {notifs.unread > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-alert text-black text-[10px] font-bold font-mono">
                                    {notifs.unread}
                                </span>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 bg-surface2 border-border">
                        <div className="flex items-center justify-between px-2 py-1.5">
                            <DropdownMenuLabel className="p-0 font-heading">Notifications</DropdownMenuLabel>
                            <button onClick={markAll} className="text-[11px] text-volt hover:underline font-mono" data-testid="mark-all-read">Mark all read</button>
                        </div>
                        <DropdownMenuSeparator className="bg-border" />
                        {notifs.notifications.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications</div>}
                        {notifs.notifications.slice(0, 6).map((n) => (
                            <div key={n.id} className={cn("px-3 py-2.5 border-b border-border/60", !n.read && "bg-volt/[0.04]")}>
                                <div className="text-sm text-white font-medium">{n.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                            </div>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button data-testid="user-menu-btn" className="flex items-center gap-2 pl-1 pr-2.5 h-9 rounded-sm border border-border hover:bg-white/5">
                            <Avatar className="w-7 h-7 rounded-sm">
                                <AvatarImage src={user?.avatar_url} />
                                <AvatarFallback className="rounded-sm bg-volt/15 text-volt text-xs font-bold">
                                    {(user?.name || "U").slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <span className="hidden sm:block text-sm text-white max-w-[120px] truncate">{user?.name}</span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-surface2 border-border">
                        <DropdownMenuLabel className="font-heading">
                            <div className="text-white">{user?.name}</div>
                            <div className="text-xs text-muted-foreground font-normal font-mono">{user?.email}</div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-border" />
                        <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer">
                            <UserCircle className="w-4 h-4 mr-2" /> Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="cursor-pointer">
                            <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-border" />
                        <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-alert focus:text-alert">
                            <LogOut className="w-4 h-4 mr-2" /> Log out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
